import * as React from "react";
import * as ProgressPrimitive from "@radix-ui/react-progress";
import { ProgressProps } from "@/components/ui/progress";

import { cn } from "@/lib/utils";

interface ProgressStage {
  label: string;
  color?: string;
  percentage: number; // Where this stage ends (0-100)
}

interface MultiStageProgressProps extends Omit<ProgressProps, "value"> {
  stages?: ProgressStage[];
  currentValue: number;
  showStageLabels?: boolean;
  stageHeight?: string;
}

const MultiStageProgress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  MultiStageProgressProps
>(
  (
    {
      className,
      stages = [],
      currentValue,
      showStageLabels = false,
      stageHeight = "h-4",
      ...props
    },
    ref,
  ) => {
    const getStageColor = (index: number) => {
      const colors = [
        "bg-blue-500",
        "bg-green-500",
        "bg-yellow-500",
        "bg-purple-500",
      ];
      return stages[index]?.color || colors[index % colors.length];
    };

    const getCurrentStageIndex = () => {
      for (let i = 0; i < stages.length; i++) {
        if (currentValue <= stages[i].percentage) {
          return i;
        }
      }
      return stages.length - 1;
    };

    const currentStageIndex = getCurrentStageIndex();

    return (
      <div className="space-y-2">
        {showStageLabels && stages.length > 0 && (
          <div className="flex justify-between text-xs text-muted-foreground">
            {stages.map((stage, index) => (
              <span
                key={index}
                className={cn(
                  "transition-colors",
                  index <= currentStageIndex
                    ? "text-foreground font-medium"
                    : "",
                )}
              >
                {stage.label}
              </span>
            ))}
          </div>
        )}
        <ProgressPrimitive.Root
          ref={ref}
          className={cn(
            `relative ${stageHeight} w-full overflow-hidden rounded-full bg-secondary`,
            className,
          )}
          {...props}
        >
          {/* Background stage indicators */}
          {stages.map((stage, index) => (
            <div
              key={index}
              className={cn(
                "absolute top-0 h-full transition-all duration-300",
                index < currentStageIndex
                  ? getStageColor(index)
                  : "bg-gray-200",
                index === 0 ? "left-0" : "",
              )}
              style={{
                left:
                  index === 0 ? "0%" : `${stages[index - 1]?.percentage || 0}%`,
                width: `${stage.percentage - (stages[index - 1]?.percentage || 0)}%`,
                opacity: index <= currentStageIndex ? 1 : 0.3,
              }}
            />
          ))}

          {/* Main progress indicator */}
          <ProgressPrimitive.Indicator
            className={cn(
              "h-full flex-1 bg-primary transition-all duration-500",
              currentStageIndex >= 0
                ? getStageColor(currentStageIndex)
                : "bg-primary",
            )}
            style={{
              transform: `translateX(-${100 - (currentValue || 0)}%)`,
              zIndex: 10,
              position: "relative",
            }}
          />

          {/* Stage separators */}
          {stages.slice(0, -1).map((stage, index) => (
            <div
              key={`separator-${index}`}
              className="absolute top-0 h-full w-0.5 bg-white/50 z-20"
              style={{ left: `${stage.percentage}%` }}
            />
          ))}
        </ProgressPrimitive.Root>
      </div>
    );
  },
);
MultiStageProgress.displayName = "MultiStageProgress";

export { MultiStageProgress, type ProgressStage };
